import { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';

import { useAuthStore } from '@/stores/authStore';

import styles from './sidebar.module.css';

function SettingsIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
  );
}

type IconName =
  | 'home'
  | 'doc'
  | 'school'
  | 'pen'
  | 'chart'
  | 'chat'
  | 'list'
  | 'userPlus'
  | 'book'
  | 'bot'
  | 'settings'
  | 'logout';

function Icon({ name }: { name: IconName }) {
  const common = {
    width: 18,
    height: 18,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.8,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
  };
  switch (name) {
    case 'home':
      return (
        <svg {...common}>
          <path d="M3 10.5 12 3l9 7.5" />
          <path d="M5 9.5V20a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V9.5" />
          <path d="M9.5 21v-6h5v6" />
        </svg>
      );
    case 'doc':
      return (
        <svg {...common}>
          <path d="M14 3H7a1 1 0 0 0-1 1v16a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1V8z" />
          <path d="M14 3v5h5" />
          <path d="M9 13h6M9 17h6" />
        </svg>
      );
    case 'school':
      return (
        <svg {...common}>
          <path d="M22 10 12 5 2 10l10 5 10-5z" />
          <path d="M6 12.5V17a6 6 0 0 0 12 0v-4.5" />
          <path d="M22 10v5" />
        </svg>
      );
    case 'pen':
      return (
        <svg {...common}>
          <path d="M12 20h9" />
          <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z" />
        </svg>
      );
    case 'chart':
      return (
        <svg {...common}>
          <path d="M3 3v18h18" />
          <path d="M7 15v-4M12 15V7M17 15v-7" />
        </svg>
      );
    case 'chat':
      return (
        <svg {...common}>
          <path d="M21 12a8 8 0 0 1-8 8H4l2-3a8 8 0 1 1 15-5z" />
          <path d="M8.5 11.5h.01M12 11.5h.01M15.5 11.5h.01" />
        </svg>
      );
    case 'list':
      return (
        <svg {...common}>
          <path d="M8 6h13M8 12h13M8 18h13" />
          <path d="M3.5 6h.01M3.5 12h.01M3.5 18h.01" />
        </svg>
      );
    case 'userPlus':
      return (
        <svg {...common}>
          <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
          <circle cx="9" cy="7" r="4" />
          <path d="M19 8v6M22 11h-6" />
        </svg>
      );
    case 'book':
      return (
        <svg {...common}>
          <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20V4H6.5A2.5 2.5 0 0 0 4 6.5v13z" />
          <path d="M4 19.5A2.5 2.5 0 0 0 6.5 22H20v-5" />
          <path d="M9 8h6" />
        </svg>
      );
    case 'bot':
      return (
        <svg {...common}>
          <rect x="4" y="8" width="16" height="12" rx="4" />
          <path d="M12 8V4" />
          <circle cx="12" cy="3" r="1" />
          <path d="M9 13.5h.01M15 13.5h.01" />
          <path d="M9.5 17h5" />
        </svg>
      );
    case 'settings':
      return (
        <svg {...common}>
          <circle cx="12" cy="12" r="3" />
          <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
        </svg>
      );
    case 'logout':
      return (
        <svg {...common}>
          <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
          <path d="M16 17l5-5-5-5" />
          <path d="M21 12H9" />
        </svg>
      );
    default:
      return null;
  }
}

interface SidebarLink {
  to: string;
  label: string;
  icon: IconName;
}

const teacherLinks: SidebarLink[] = [
  { to: '/', label: '工作台', icon: 'home' },
  { to: '/jobs', label: '作业管理', icon: 'doc' },
  { to: '/classroom', label: '班级管理', icon: 'school' },
  { to: '/review/queue', label: '批阅中心', icon: 'pen' },
  { to: '/feedback', label: '申诉处理', icon: 'chat' },
  { to: '/class/analytics', label: '班级分析', icon: 'chart' },
  { to: '/assistant', label: 'AI 助手', icon: 'bot' },
];

const studentLinks: SidebarLink[] = [
  { to: '/', label: '工作台', icon: 'home' },
  { to: '/jobs', label: '作业列表', icon: 'list' },
  { to: '/todo', label: '待办中心', icon: 'pen' },
  { to: '/mistakes', label: '错题本', icon: 'book' },
  { to: '/report', label: '学情报告', icon: 'chart' },
  { to: '/my-classes', label: '我的班级', icon: 'school' },
  { to: '/join', label: '加入班级', icon: 'userPlus' },
  { to: '/feedback', label: '我的反馈', icon: 'chat' },
  { to: '/assistant', label: 'AI 助手', icon: 'bot' },
];

export function Sidebar() {
  const user = useAuthStore((state) => state.user);
  const clearAuth = useAuthStore((state) => state.clearAuth);
  const navigate = useNavigate();
  const [avatarError, setAvatarError] = useState(false);
  const isStudent = user?.role === 'student';
  const links = isStudent ? studentLinks : teacherLinks;

  const handleLogout = () => {
    clearAuth();
    navigate('/login');
  };

  return (
    <aside className={styles.sidebar}>
      <div className={styles.brand}>
        <span className={styles.brandLogo}>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <rect x="3" y="3" width="18" height="18" rx="5" fill="currentColor" />
            <path
              d="M12 7.5 9.6 16.5M12 7.5 14.4 16.5M10.4 13h3.2"
              stroke="#ffffff"
              strokeWidth="1.6"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
        <span className={styles.brandText}>AI 智能批阅</span>
      </div>

      <nav className={styles.nav}>
        <div className={styles.navLabel}>导航菜单</div>
        {links.map((link) => (
          <NavLink
            key={link.to}
            to={link.to}
            end={link.to === '/'}
            className={({ isActive }) =>
              isActive ? `${styles.link} ${styles.active}` : styles.link
            }
          >
            <span className={styles.linkIcon}>
              <Icon name={link.icon} />
            </span>
            {link.label}
          </NavLink>
        ))}
      </nav>

      <div className={styles.footer}>
        <div className={styles.userCard}>
          {user?.avatar_url && !avatarError ? (
            <img
              src={user.avatar_url}
              alt={user.display_name}
              className={styles.avatar}
              onError={() => setAvatarError(true)}
            />
          ) : (
            <div className={styles.avatar}>{user?.display_name?.[0] || '?'}</div>
          )}
          <div className={styles.userInfo}>
            <div className={styles.name}>{user?.nickname || user?.display_name}</div>
            <div className={styles.role}>{isStudent ? '学生' : '教师'}</div>
          </div>
          <button
            type="button"
            className={styles.settingsBtn}
            title="账号设置"
            aria-label="账号设置"
            onClick={() => navigate('/settings')}
          >
            <SettingsIcon />
          </button>
        </div>
        <button type="button" className={styles.logout} onClick={handleLogout}>
          <Icon name="logout" />
          退出登录
        </button>
      </div>
    </aside>
  );
}
